import { Injectable } from "@angular/core";
import {State, Action, StateContext, Selector, createSelector, Store} from "@ngxs/store";
import { ShellStateModel } from "./shell.model";
import {Shell} from "./shell.actions";
import { ThemeType } from "../../models/enums/theme.enum";
import { MenuEntry } from "src/app/modules/protected/components/menu/models/menu.model";
import { flatMapDeep } from "lodash-es";
import { TecComModuleType, User } from "shared-states";
import { Router } from "@angular/router";
import { AuthFlowService } from "../../navigation/auth/auth-flow.service";

function getAllPaths(menuEntries: MenuEntry[]): string[] {
  return flatMapDeep(menuEntries, (menuEntry: MenuEntry) => {
    return [menuEntry.path, ...getAllPaths(menuEntry.entries)];
  });
}

@State<ShellStateModel>({
  name: "shell",
  defaults: {
    defaultMenu: [],
    userMenu: [],
    theme: ThemeType.light,
    isInitialized: false,
    activeMenuRoutePath: ''
  },
})
@Injectable()
export class ShellState {
  @Selector()
  static getState(state: ShellStateModel): ShellStateModel {
    return state;
  }

  @Selector()
  static isInitialized(state: ShellStateModel): boolean {
    return state.isInitialized;
  }

  @Selector()
  static getStateError(state: ShellStateModel): Error | undefined {
    return state.error;
  }

  @Selector()
  static getTheme(state: ShellStateModel): ThemeType {
    return state.theme;
  }
  @Selector()
  static isDarkTheme(state: ShellStateModel): boolean {
    return state.theme === ThemeType.dark;
  }

  @Selector()
  static getLogoIcon(state: ShellStateModel): string {
    return 'teccom-logo-2026';
  }

  @Selector()
  static getUserMenu(state: ShellStateModel): MenuEntry[] {
    return state.userMenu;
  }

  @Selector()
  static getUserSubMenu(state: ShellStateModel): MenuEntry[] {
    const byActiveModule = state.userMenu.find((menu: MenuEntry) => menu.module === state.activeModule)?.entries;
    const byActiveMenuPath = state.userMenu.find(menu => menu.path === state.activeMenuRoutePath)?.entries;
    return byActiveModule ?? byActiveMenuPath ?? [];
  }


  static hasPathPermission(menuPath: string): (state: ShellStateModel) => boolean {
    return createSelector([ShellState], (state: ShellStateModel) => {
      return getAllPaths(state.userMenu).filter((p: string) => p.indexOf('/') >= 0).some((path: string) => menuPath.includes(`/${path}`));
    });
  }

  static isModuleActive(module: TecComModuleType): (state: ShellStateModel) => boolean {
    return createSelector([ShellState], (state: ShellStateModel) => {
      return module === state.activeModule;
    });
  }

  static isParentMenuHighlight(module: TecComModuleType, path: string): (state: ShellStateModel) => boolean {
    return createSelector([ShellState], (state: ShellStateModel) => {
      const isActiveModule = module === state.activeModule;
      const isActiveMenuPath = state.activeMenuRoutePath.startsWith(`/${path}`);
      return isActiveModule || isActiveMenuPath;
    });
  }

  static isChildMenuHighlight(path: string): (state: ShellStateModel) => boolean {
    return createSelector([ShellState], (state: ShellStateModel) => {
      return state.activeMenuRoutePath.startsWith(`/${path}`)
        || window.location.pathname.startsWith(`/${path}`);
    });
  }

  @Selector()
  static getActiveMenuRoutePath(state: ShellStateModel): string {
    return state.activeMenuRoutePath;
  }

  constructor(
    private authFlow: AuthFlowService,
    private router: Router,
  ) { }

  @Action(Shell.InitShellSuccessAction)
  initShellSuccessAction(
    { patchState }: StateContext<ShellStateModel>,
    { theme, menu, userMenu, error }: Shell.InitShellSuccessAction
  ): void {
    patchState({ theme: theme, defaultMenu: menu, userMenu: userMenu, isInitialized: true, error: error });
  }

  @Action(Shell.UpdateUserMenuSuccessAction)
  updateUserMenuSuccessAction(
    { patchState }: StateContext<ShellStateModel>,
    { menu }: Shell.UpdateUserMenuSuccessAction
  ): void {
    patchState({ userMenu: menu });
  }

  @Action(Shell.ChangeThemeSuccessAction)
  changeThemeSuccessAction(
    { patchState }: StateContext<ShellStateModel>,
    { theme }: Shell.ChangeThemeSuccessAction
  ): void {
    patchState({ theme: theme });
  }

  @Action(Shell.ChangeActiveModuleSuccessAction)
  changeActiveModuleSuccessAction(
    { patchState }: StateContext<ShellStateModel>,
    { module }: Shell.ChangeActiveModuleSuccessAction
  ): void {
    patchState({ activeModule: module });
  }

  @Action(User.UserSessionExpiredAction)
  async processSessionExpired(): Promise<void> {
    const redirect = await this.authFlow.requireSignIn(this.router.url);
    if (redirect) {
      await this.router.navigateByUrl(redirect);
    }
  }

  @Action(Shell.UpdateUserMenuActiveRoutePath)
  updateUserMenuActiveRoutePath(context: StateContext<ShellStateModel>, action: Shell.UpdateUserMenuActiveRoutePath): void {
    context.patchState({ activeMenuRoutePath: action.activeRoutePath });
  }

  @Action(Shell.SetErrorAction)
  setError(context: StateContext<ShellStateModel>, action: Shell.SetErrorAction): void {
    context.patchState({ error: action.error });
  }

}
